/**
 * Audit Session Modal
 *
 * The auditor's counterpart to ValidateSessionModal: a resumable walk over the fields a validator
 * already reviewed, asking whose answer was right — the user's, the LLM's, both, or neither.
 *
 * Each "Next" saves via PUT /api/sessions/{id}/audit, so progress persists and the popup resumes
 * where the auditor left off. Nothing here changes the session's values: a "neither correct"
 * confirmed answer is a measurement, never merged into the field stores.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  AuditFieldInput,
  AuditResult,
  Session,
  auditSessionFields,
} from '../../../store/sessionSlice';
import { showError, showSuccess } from '../../../store/toastsSlice';
import {
  FieldValue,
  fieldValuesEqual,
  isFieldPending,
  mergeWithHumanOverrides,
  parentValueOf,
} from '../../../utils/fieldUtils';
import { FONT_SIZE_BODY, TEXT_COLOR_PRIMARY } from '../../shared/cssConstants';
import AuditGroupEditor from './AuditGroupEditor';
import StepReviewModal from './StepReviewModal';
import { auditSteps } from './auditSteps';

interface AuditSessionModalProps {
  open: boolean;
  session: Session;
  onClose: () => void;
}

const AuditSessionModal: React.FC<AuditSessionModalProps> = ({ open, session, onClose }) => {
  const dispatch = useAppDispatch();
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const [stepIndex, setStepIndex] = useState(0);
  const [resultsByGroup, setResultsByGroup] = useState<Record<string, AuditResult>>({});
  const [confirmedByField, setConfirmedByField] = useState<Record<string, FieldValue>>({});
  const [commentsByGroup, setCommentsByGroup] = useState<Record<string, string>>({});
  // Groups whose recorded decision the auditor has opened for override, keyed by group root.
  const [editingByGroup, setEditingByGroup] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);

  const extracted = (session.extracted_fields || {}) as Record<string, FieldValue>;
  const audited = session.audited_fields || {};

  // What the validating user ended up with — the same precedence the session detail shows.
  const displayed = useMemo(
    () =>
      mergeWithHumanOverrides(
        session.extracted_fields || {},
        session.human_overrides_fields,
        session.submitted_fields,
      ) as Record<string, FieldValue>,
    [session.extracted_fields, session.human_overrides_fields, session.submitted_fields],
  );

  // Every validated field, already-audited ones included so the auditor can navigate back.
  // Derived, not set from an effect: `steps` also drives the "nothing to audit" guard, and
  // computing it a commit late flashed the empty state on first open.
  const steps = useMemo(
    () => (fieldsConfig ? auditSteps(session, fieldsConfig) : []),
    [session, fieldsConfig],
  );

  // Start at the first group still holding an unaudited field, and seed the recorded decisions.
  useEffect(() => {
    if (!open || !fieldsConfig) return;

    const firstUnaudited = steps.findIndex((group) => group.some((field) => !audited[field]));
    setStepIndex(firstUnaudited === -1 ? 0 : firstUnaudited);
    // Seed from any results already recorded so resuming shows what was decided.
    const resultSeed: Record<string, AuditResult> = {};
    const confirmedSeed: Record<string, FieldValue> = {};
    const commentSeed: Record<string, string> = {};
    for (const [field, entry] of Object.entries(audited)) {
      resultSeed[field] = entry.result;
      if (entry.confirmed_value !== undefined) confirmedSeed[field] = entry.confirmed_value;
      if (entry.comment) commentSeed[field] = entry.comment;
    }
    setResultsByGroup(resultSeed);
    setConfirmedByField(confirmedSeed);
    setCommentsByGroup(commentSeed);
    setEditingByGroup({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fieldsConfig]);

  const currentGroup: string[] = steps[stepIndex] || [];
  // The group's result and comment are held under its hierarchy root, the way validation keys
  // a step's state on one of its fields.
  const groupKey = currentGroup[0] || '';
  const isLastStep = stepIndex >= steps.length - 1;
  const allFields = ([] as string[]).concat(...steps);
  const auditedCount = allFields.filter((field) => audited[field]).length;

  const parentValueFor = (fieldName: string) => parentValueOf(fieldsConfig, fieldName, displayed);

  const handleNext = async () => {
    if (currentGroup.length === 0) return;

    const result = resultsByGroup[groupKey];
    if (!result) {
      dispatch(showError('Please choose which answer was correct.'));
      return;
    }

    const needsConfirmedValue =
      result === 'neither_correct' &&
      currentGroup.some((field) => isFieldPending(confirmedByField[field]));
    if (needsConfirmedValue) {
      dispatch(showError('Please provide the correct answer for every field shown.'));
      return;
    }

    const comment = (commentsByGroup[groupKey] || '').trim();
    // Revisiting a group with nothing changed: no request, just advance.
    const unchanged = currentGroup.every((field) => {
      const prior = audited[field];
      return (
        prior &&
        prior.result === result &&
        (prior.comment || '') === comment &&
        fieldValuesEqual(confirmedByField[field], prior.confirmed_value as FieldValue | undefined)
      );
    });
    if (unchanged) {
      if (isLastStep) onClose();
      else setStepIndex((index) => index + 1);
      return;
    }

    // One judgement, recorded against each field in the group so every metrics row stands alone.
    // The confirmed value is per field; the result and comment are the group's.
    const fields: Record<string, AuditFieldInput> = {};
    for (const field of currentGroup) {
      const payload: AuditFieldInput = { result };
      if (result === 'neither_correct') payload.confirmed_value = confirmedByField[field];
      if (comment) payload.comment = comment;
      fields[field] = payload;
    }

    setSaving(true);
    try {
      await dispatch(auditSessionFields({ sessionId: session.session_id, fields })).unwrap();
      // Close the override back up: the decision is recorded, so coming back here must show the
      // guarded summary again rather than live radios a stray click could re-save.
      setEditingByGroup((prev) => ({ ...prev, [groupKey]: false }));
      if (isLastStep) {
        dispatch(showSuccess('Audit saved'));
        onClose();
      } else {
        setStepIndex((index) => index + 1);
      }
    } catch (error: any) {
      dispatch(showError(typeof error === 'string' ? error : 'Failed to save audit'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // The header only offers an audit once the session is validated, so this is belt-and-braces.
  if (fieldsConfig && steps.length === 0) {
    return (
      <Modal open={open} onClose={onClose} title="Audit Session" scrollContainer="modal" closeLabel="Close">
        <div style={{ minWidth: '420px', maxWidth: '560px' }}>
          <div style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY }}>
            This session has no validated fields to audit yet.
          </div>
        </div>
        <ModalFooter>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" size="small" onClick={onClose}>
              Got it
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  if (steps.length === 0) return null;

  return (
    <StepReviewModal
      open={open}
      onClose={onClose}
      evidenceFields={currentGroup}
      heading="Audit"
      progress={`Fields Audited: ${auditedCount} of ${allFields.length}`}
      onPrevious={() => setStepIndex((index) => Math.max(0, index - 1))}
      onNext={handleNext}
      nextLabel={isLastStep ? 'Save & Finish' : 'Save & Next →'}
      busy={saving}
      canGoBack={stepIndex > 0}
    >
      {currentGroup.length > 0 && (
        <AuditGroupEditor
          key={groupKey}
          fields={currentGroup}
          llmValues={extracted}
          userValues={displayed}
          parentValueFor={parentValueFor}
          answersAgree={currentGroup.every((field) =>
            fieldValuesEqual(displayed[field], extracted[field]),
          )}
          result={resultsByGroup[groupKey]}
          onResultChange={(result) => setResultsByGroup((prev) => ({ ...prev, [groupKey]: result }))}
          confirmedValues={confirmedByField}
          onConfirmedValueChange={(fieldName, value) =>
            setConfirmedByField((prev) => ({ ...prev, [fieldName]: value }))
          }
          comment={commentsByGroup[groupKey] || ''}
          onCommentChange={(comment) =>
            setCommentsByGroup((prev) => ({ ...prev, [groupKey]: comment }))
          }
          priorAudit={audited[groupKey]}
          editing={!!editingByGroup[groupKey]}
          onEditToggle={() =>
            setEditingByGroup((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }))
          }
        />
      )}
    </StepReviewModal>
  );
};

export default AuditSessionModal;
